import React, { useState, useMemo, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { FiTrendingUp, FiUsers, FiAward, FiLayers, FiDownload } from 'react-icons/fi';
import axios from 'axios';
import BASE_URL from '../../url';
import '../styles/SubmissionReport.css';

// ── Palette ───────────────────────────────────
const COLORS = [
  '#3b5bfd', '#3b82f6', '#f59e0b', '#ec4899',
  '#8b5cf6', '#ef4444', '#14b8a6', '#f97316',
  '#84cc16', '#06b6d4', '#6366f1'
];

// ── Submissions Detail Modal (reused from ManagerDashboard) ────────────────
const SubmissionsModal = ({ recruiter, startDate, endDate, onClose }) => {
  const [submissions, setSubmissions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${BASE_URL}/api/recruitment/applications/job-report/submissions`,
          { params: { recruiterId: recruiter.id, startDate, endDate }, headers: { Authorization: `Bearer ${token}` } }
        );
        setSubmissions(response.data.map(role => ({
          id: role.id,
          externalJobRef: role.externalJobRef || role.jobId || 'N/A',
          clientName: role.clientName || 'N/A',
          jobTitle: role.jobTitle || 'N/A',
          createdAt: role.createdAt,
        })));
      } catch {
        setError('Failed to load submissions.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [recruiter, startDate, endDate]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '12px', width: '680px', maxWidth: '95vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>Submissions by {recruiter.name}</h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>{startDate} → {endDate}</p>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '16px', color: '#64748b' }}>✕</button>
        </div>
        <div style={{ overflowY: 'auto', padding: '12px 20px' }}>
          {loading ? <p style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>Loading...</p> :
           error ? <p style={{ color: '#ef4444', textAlign: 'center' }}>{error}</p> :
           submissions.length === 0 ? <p style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>No submissions found.</p> :
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', color: '#64748b', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase' }}>Job Ref</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', color: '#64748b', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase' }}>Client</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', color: '#64748b', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase' }}>Title</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', color: '#64748b', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((s, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 10px', color: '#3b5bfd', fontWeight: '500' }}>{s.externalJobRef}</td>
                  <td style={{ padding: '8px 10px' }}>{s.clientName}</td>
                  <td style={{ padding: '8px 10px' }}>{s.jobTitle}</td>
                  <td style={{ padding: '8px 10px', color: '#94a3b8' }}>{s.createdAt ? new Date(s.createdAt).toLocaleDateString() : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>}
        </div>
      </div>
    </div>
  );
};

// ── Custom Pie Label ───────────────────────────────────
const RADIAN = Math.PI / 180;
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// ── Week number helper ─────────────────────────────────
const getWeekNumber = (d) => {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
};

// ── Main Component ─────────────────────────────────────
const SubmissionReport = ({ reportData = [], trendData = [], isLoading, startDate, endDate, activeFilter, onFilterChange }) => {
  const [period, setPeriod] = useState(activeFilter && activeFilter !== 'custom' ? activeFilter : null);
  const [scope, setScope] = useState('individual'); // 'individual' | 'team'
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState(null); // null = all teams

  // Sync period with parent's activeFilter when it changes
  React.useEffect(() => {
    if (activeFilter && activeFilter !== 'custom') {
      setPeriod(activeFilter);
    } else if (activeFilter === 'custom') {
      setPeriod(null);
    }
  }, [activeFilter]);

  // ── Flat members list ──────────────────────────────
  const allMembers = useMemo(() => {
    const list = [];
    reportData.forEach(team => {
      (team.members || []).forEach(m => {
        if (!list.find(x => x.id === m.id)) {
          list.push({ id: m.id, name: m.name, submissions: m.submissionCount, team: team.teamName });
        }
      });
    });
    return list;
  }, [reportData]);

  // ── KPI values ───────────────────────────────────
  const totalSubmissions = allMembers.reduce((s, m) => s + m.submissions, 0);
  const topRecruiter = [...allMembers].sort((a, b) => b.submissions - a.submissions)[0] || { name: '—', submissions: 0 };
  const topTeam = [...reportData].sort((a, b) => b.totalSubmissions - a.totalSubmissions)[0] || { teamName: '—', totalSubmissions: 0 };
  const activeRecruiters = allMembers.filter(m => m.submissions > 0).length;

  // ── Chart data from trendData ─────────────────────
  const { chartData, chartKeys } = useMemo(() => {
    if (!trendData || trendData.length === 0) return { chartData: [], chartKeys: [] };

    const trendMap = {};
    const keySet = new Set();

    trendData.forEach(item => {
      const d = new Date(item.date);
      let groupKey = item.date;
      let displayStr = d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
      let sortDate = item.date;

      const activePeriod = period;
      if (activePeriod === 'weekly') {
        const wk = getWeekNumber(d);
        const day = d.getUTCDay();
        const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), diff));
        groupKey = `W${wk}-${monday.getUTCFullYear()}`;
        displayStr = `W${wk} (${monday.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })})`;
        sortDate = monday.toISOString().split('T')[0];
      } else if (activePeriod === 'monthly') {
        groupKey = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
        displayStr = d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', year: 'numeric' });
        sortDate = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().split('T')[0];
      }

      if (!trendMap[groupKey]) trendMap[groupKey] = { name: displayStr, _sort: sortDate };

      if (scope === 'team') {
        // Group by team name
        const member = allMembers.find(m => m.name === item.recruiterName);
        const teamName = member ? member.team : 'Other';
        trendMap[groupKey][teamName] = (trendMap[groupKey][teamName] || 0) + item.submissions;
        keySet.add(teamName);
      } else {
        trendMap[groupKey][item.recruiterName] = (trendMap[groupKey][item.recruiterName] || 0) + item.submissions;
        keySet.add(item.recruiterName);
      }
    });

    const sorted = Object.values(trendMap).sort((a, b) => new Date(a._sort) - new Date(b._sort));
    return { chartData: sorted, chartKeys: Array.from(keySet) };
  }, [trendData, period, scope, allMembers]);

  // ── Pie data (team distribution) ─────────────────
  const pieData = useMemo(() => {
    return reportData
      .filter(t => t.totalSubmissions > 0)
      .map(t => ({ name: t.teamName, value: t.totalSubmissions }))
      .sort((a, b) => b.value - a.value);
  }, [reportData]);

  // ── Table rows based on scope + team filter ───────
  const tableRows = useMemo(() => {
    if (scope === 'individual') {
      const rows = selectedTeamFilter
        ? allMembers.filter(m => m.team === selectedTeamFilter)
        : allMembers;
      return [...rows].sort((a, b) => b.submissions - a.submissions);
    } else {
      return [...reportData]
        .map(t => ({ name: t.teamName, submissions: t.totalSubmissions, team: '' }))
        .sort((a, b) => b.submissions - a.submissions);
    }
  }, [scope, allMembers, reportData, selectedTeamFilter]);

  // ── Period change handler ─────────────────────────
  const handlePeriodChange = (p) => {
    setPeriod(p);
    onFilterChange && onFilterChange(p);
  };

  const rankClass = (i) => i === 0 ? 'r1' : i === 1 ? 'r2' : i === 2 ? 'r3' : '';

  // ── Download full report as formatted HTML ────────
  const downloadReport = useCallback(() => {
    const periodLabel = period.charAt(0).toUpperCase() + period.slice(1);
    const scopeLabel = scope === 'individual' ? 'Individual' : 'Team-wise';
    const dateRange = `${startDate || ''} to ${endDate || ''}`;

    // Build table rows HTML
    const rowsHtml = tableRows.map((row, i) => `
      <tr style="border-bottom:1px solid #f1f5f9; ${i % 2 === 0 ? 'background:#fafafa;' : ''} ">
        <td style="padding:10px 14px; font-weight:700; color:${i===0?'#d97706':i===1?'#64748b':i===2?'#b91c1c':'#374151'}">${i + 1}</td>
        <td style="padding:10px 14px; font-weight:500; color:#0f172a">${row.name}</td>
        ${scope === 'individual' ? `<td style="padding:10px 14px; color:#64748b">${row.team}</td>` : ''}
        <td style="padding:10px 14px">
          <span style="background:#eff6ff;color:#2563eb;font-weight:700;padding:4px 12px;border-radius:20px;font-size:13px">${row.submissions}</span>
        </td>
      </tr>
    `).join('');

    // Build time-series table from chartData
    const timeTableHeaders = chartKeys.slice(0, 10).map(k => `<th style="padding:8px 10px;text-align:right;color:#64748b;font-size:11px;text-transform:uppercase">${k}</th>`).join('');
    const timeTableRows = chartData.map(row => {
      const cells = chartKeys.slice(0, 10).map(k =>
        `<td style="padding:8px 10px;text-align:right;font-weight:${row[k]>0?'600':'400'};color:${row[k]>0?'#0f172a':'#cbd5e1'}">${row[k] || 0}</td>`
      ).join('');
      return `<tr style="border-bottom:1px solid #f1f5f9"><td style="padding:8px 10px;font-weight:500;color:#374151;white-space:nowrap">${row.name}</td>${cells}</tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Submission Analytics Report — ${dateRange}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', sans-serif; background: #fff; color: #1e293b; padding: 32px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; border-bottom: 2px solid #3b5bfd; padding-bottom: 16px; }
    h1 { font-size: 22px; font-weight: 700; color: #0f172a; }
    .meta { font-size: 13px; color: #64748b; margin-top: 4px; }
    .download-pdf-btn {
      background: #3b5bfd; color: #fff; border: none; padding: 8px 18px; border-radius: 6px;
      font-weight: 600; font-size: 13px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
    }
    .download-pdf-btn:hover { background: #0f766e; }
    .badges { display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; }
    .badge { padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
    .badge.period { background: #eff6ff; color: #2563eb; }
    .badge.scope  { background: #eff6ff; color: #2563eb; }
    .kpi-row { display: flex; gap: 16px; margin-bottom: 28px; flex-wrap: wrap; }
    .kpi { flex: 1; min-width: 140px; border-radius: 10px; padding: 16px 18px; border-left: 4px solid; }
    .kpi.teal  { border-color: #3b5bfd; background: #eff6ff; }
    .kpi.blue  { border-color: #3b82f6; background: #eff6ff; }
    .kpi.gold  { border-color: #f59e0b; background: #fffbeb; }
    .kpi.pink  { border-color: #ec4899; background: #fdf2f8; }
    .kpi-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8; margin-bottom: 6px; }
    .kpi-val   { font-size: 24px; font-weight: 700; color: #0f172a; }
    .kpi-sub   { font-size: 12px; color: #64748b; margin-top: 4px; }
    h2 { font-size: 15px; font-weight: 600; color: #0f172a; margin-bottom: 12px; border-left: 3px solid #3b5bfd; padding-left: 10px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 32px; }
    thead th { background: #f8fafc; padding: 10px 14px; text-align: left; font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: 600; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; text-align: center; }
    @media print { body { padding: 20px; } .download-pdf-btn { display: none !important; } }
  </style>
</head>
<body>
  <div class="report-container">
    <div class="header">
      <div>
        <h1>📊 Submission Analytics Report</h1>
        <p class="meta">Period: <strong>${dateRange}</strong> &nbsp;·&nbsp; Generated: <strong>${new Date().toLocaleString()}</strong></p>
      </div>
      <button class="download-pdf-btn" id="downloadPdfBtn" onclick="downloadAsPDF()">📥 Download as PDF</button>
    </div>
    <div class="badges">
      <span class="badge period">${periodLabel} View</span>
      <span class="badge scope">${scopeLabel}</span>
    </div>

    <div class="kpi-row">
      <div class="kpi teal"><div class="kpi-label">Total Submissions</div><div class="kpi-val">${totalSubmissions}</div><div class="kpi-sub">Selected period</div></div>
      <div class="kpi blue"><div class="kpi-label">Active Recruiters</div><div class="kpi-val">${activeRecruiters}</div><div class="kpi-sub">With ≥ 1 submission</div></div>
      <div class="kpi gold"><div class="kpi-label">Top Recruiter</div><div class="kpi-val" style="font-size:17px">${topRecruiter.name}</div><div class="kpi-sub">${topRecruiter.submissions} submissions</div></div>
      <div class="kpi pink"><div class="kpi-label">Best Team</div><div class="kpi-val" style="font-size:17px">${topTeam.teamName}</div><div class="kpi-sub">${topTeam.totalSubmissions} submissions</div></div>
    </div>

    <h2>${periodLabel} Breakdown by ${scope === 'individual' ? 'Recruiter' : 'Team'}</h2>
    <table>
      <thead>
        <tr>
          <th>Period</th>
          ${timeTableHeaders}
        </tr>
      </thead>
      <tbody>${timeTableRows || '<tr><td colspan="20" style="padding:12px;color:#94a3b8;text-align:center">No data</td></tr>'}</tbody>
    </table>

    <h2>${scopeLabel} Performance Ranking</h2>
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>${scope === 'individual' ? 'Recruiter' : 'Team'}</th>
          ${scope === 'individual' ? '<th>Team</th>' : ''}
          <th>Submissions</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>

    <div class="footer">Prophecy ERP — Submission Analytics Report &nbsp;|&nbsp; ${new Date().toLocaleDateString()}</div>
  </div>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
  <script>
    function downloadAsPDF() {
      var btn = document.getElementById('downloadPdfBtn');
      if (btn) btn.style.display = 'none';
      var element = document.querySelector('.report-container') || document.body;
      var opt = {
        margin: [8, 8, 8, 8],
        filename: 'Submission_Analytics_Report_${dateRange}.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      if (window.html2pdf) {
        html2pdf().set(opt).from(element).save().then(function() {
          if (btn) btn.style.display = '';
        }).catch(function() {
          if (btn) btn.style.display = '';
          window.print();
        });
      } else {
        window.print();
        if (btn) btn.style.display = '';
      }
    }
  </script>
</body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `SubmissionReport_${period}_${scopeLabel}_${startDate}_to_${endDate}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [tableRows, chartData, chartKeys, period, scope, startDate, endDate, totalSubmissions, activeRecruiters, topRecruiter, topTeam]);

  // ── Download individual recruiter report ──────────
  const downloadRecruiterReport = useCallback(async (row) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/recruitment/applications/job-report/submissions`,
        { params: { recruiterId: row.id, startDate, endDate }, headers: { Authorization: `Bearer ${token}` } }
      );
      const subs = response.data;

      const buckets = {};
      subs.forEach(s => {
        if (!s.createdAt) return;
        const d = new Date(s.createdAt);
        let label;
        if (period === 'weekly') {
          // Use Mon–Sun date range as the label so same-week dates always get the same key
          const day = d.getDay();
          const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday of the week
          const monday = new Date(d);
          monday.setDate(diff);
          const sunday = new Date(monday);
          sunday.setDate(monday.getDate() + 6);
          const opts = { month: 'short', day: 'numeric' };
          label = `${monday.toLocaleDateString('en-US', opts)} – ${sunday.toLocaleDateString('en-US', opts)}, ${monday.getFullYear()}`;
        } else if (period === 'monthly') {
          label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        } else {
          // daily
          label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        }
        if (!buckets[label]) buckets[label] = [];
        buckets[label].push(s);
      });

      // Build human-readable week range label
      const getWeekRangeLabel = (d) => {
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
        const monday = new Date(d);
        monday.setDate(diff);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        const opts = { month: 'short', day: 'numeric' };
        return `${monday.toLocaleDateString('en-US', opts)} – ${sunday.toLocaleDateString('en-US', opts)}, ${monday.getFullYear()}`;
      };

      const periodRows = Object.entries(buckets).map(([bucketLabel, items]) => `
        <tr style="background:#f8fafc">
          <td colspan="4" style="padding:8px 14px;font-weight:700;color:#3b5bfd;border-top:2px solid #e2e8f0">${bucketLabel} — ${items.length} submission${items.length !== 1 ? 's' : ''}</td>
        </tr>
        ${items.map(s => `
          <tr style="border-bottom:1px solid #f1f5f9">
            <td style="padding:8px 14px;color:#3b5bfd">${s.externalJobRef || s.jobId || 'N/A'}</td>
            <td style="padding:8px 14px">${s.clientName || 'N/A'}</td>
            <td style="padding:8px 14px">${s.jobTitle || 'N/A'}</td>
            <td style="padding:8px 14px;color:#64748b">${s.createdAt ? new Date(s.createdAt).toLocaleDateString() : 'N/A'}</td>
          </tr>`).join('')}
      `).join('');

      const viewLabel = period ? (period.charAt(0).toUpperCase() + period.slice(1)) : 'Daily';

      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${row.name} — Submission Report</title>
  <style>
    * { box-sizing:border-box; margin:0; padding:0; }
    body { font-family:'Segoe UI',sans-serif; background:#fff; color:#1e293b; padding:32px; }
    h1 { font-size:22px; font-weight:700; color:#0f172a; }
    .meta { font-size:13px; color:#64748b; margin:4px 0 24px; }
    .summary { display:flex; gap:16px; margin-bottom:28px; flex-wrap:wrap; }
    .sum-card { flex:1; min-width:140px; border-radius:10px; padding:14px 18px; border-left:4px solid #3b5bfd; background:#eff6ff; }
    .sum-label { font-size:11px; text-transform:uppercase; color:#94a3b8; margin-bottom:4px; }
    .sum-val { font-size:22px; font-weight:700; color:#0f172a; }
    table { width:100%; border-collapse:collapse; }
    thead th { background:#f8fafc; padding:10px 14px; text-align:left; font-size:11px; text-transform:uppercase; color:#64748b; font-weight:600; }
    .footer { margin-top:40px; padding-top:16px; border-top:1px solid #e2e8f0; font-size:11px; color:#94a3b8; text-align:center; }
    @media print { body { padding:20px; } }
  </style>
</head>
<body>
  <h1>👤 ${row.name} — Submission Report</h1>
  <p class="meta">Team: <strong>${row.team || 'N/A'}</strong> &nbsp;·&nbsp; Period: <strong>${startDate} to ${endDate}</strong> &nbsp;·&nbsp; View: <strong>${viewLabel}</strong></p>
  <div class="summary">
    <div class="sum-card"><div class="sum-label">Total Submissions</div><div class="sum-val">${subs.length}</div></div>
    <div class="sum-card" style="border-color:#f59e0b;background:#fffbeb"><div class="sum-label">${viewLabel} Periods</div><div class="sum-val">${Object.keys(buckets).length}</div></div>
  </div>
  <table>
    <thead><tr><th>Job Ref</th><th>Client</th><th>Job Title</th><th>Date</th></tr></thead>
    <tbody>${periodRows || '<tr><td colspan="4" style="padding:12px;color:#94a3b8;text-align:center">No submissions found</td></tr>'}</tbody>
  </table>
  <div class="footer">Prophecy ERP &nbsp;|&nbsp; ${row.name} &nbsp;|&nbsp; Generated ${new Date().toLocaleString()}</div>
</body></html>`;

      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${row.name.replace(/\s+/g, '_')}_Report_${period || 'custom'}_${startDate}_to_${endDate}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download report. Please try again.');
    }
  }, [period, startDate, endDate]);

  return (
    <div className="submission-report">
      {/* Submissions Detail Modal */}
      {selectedRecruiter && (
        <SubmissionsModal
          recruiter={selectedRecruiter}
          startDate={startDate}
          endDate={endDate}
          onClose={() => setSelectedRecruiter(null)}
        />
      )}

      {/* ── Header ─────────────────── */}
      <div className="sr-header">
        <div>
          <h2>Submission Analytics Report</h2>
          <p>
            {startDate && endDate ? `${startDate} → ${endDate}` : 'Select a date range above'}
            {selectedTeamFilter && <> &nbsp;·&nbsp; Filtered: <strong>{selectedTeamFilter}</strong> <button onClick={() => setSelectedTeamFilter(null)} style={{ border: 'none', background: 'none', color: '#3b5bfd', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>✕ Clear</button></>}
          </p>
        </div>
        <div className="sr-controls">
          {/* Scope toggle */}
          <div className="sr-toggle">
            {['individual', 'team'].map(s => (
              <button key={s} className={scope === s ? 'active' : ''} onClick={() => setScope(s)}>
                {s === 'individual' ? '👤 Individual' : '👥 Team-wise'}
              </button>
            ))}
          </div>
          {/* Period toggle */}
          <div className="sr-toggle">
            {['daily', 'weekly', 'monthly'].map(p => (
              <button key={p} className={period === p ? 'active' : ''} onClick={() => handlePeriodChange(p)}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
          {/* Download full report */}
          <button
            className="sr-download-btn"
            onClick={downloadReport}
            title="Download full report as HTML (open in browser to Print/Save as PDF)"
          >
            <FiDownload size={14} /> Download Report
          </button>
        </div>
      </div>

      {/* ── KPI Cards ─────────────────── */}
      <div className="sr-kpi-grid">
        <div className="sr-kpi-card teal">
          <div className="sr-kpi-label"><FiTrendingUp style={{ marginRight: 4 }} />Total Submissions</div>
          <div className="sr-kpi-value">{isLoading ? '—' : totalSubmissions}</div>
          <div className="sr-kpi-sub">Selected period</div>
        </div>
        <div className="sr-kpi-card blue">
          <div className="sr-kpi-label"><FiUsers style={{ marginRight: 4 }} />Active Recruiters</div>
          <div className="sr-kpi-value">{isLoading ? '—' : activeRecruiters}</div>
          <div className="sr-kpi-sub">With ≥ 1 submission</div>
        </div>
        <div className="sr-kpi-card gold">
          <div className="sr-kpi-label"><FiAward style={{ marginRight: 4 }} />Top Recruiter</div>
          <div className="sr-kpi-value name">{isLoading ? '—' : topRecruiter.name}</div>
          <div className="sr-kpi-sub gold">{isLoading ? '' : `${topRecruiter.submissions} submissions`}</div>
        </div>
        <div className="sr-kpi-card pink">
          <div className="sr-kpi-label"><FiLayers style={{ marginRight: 4 }} />Best Team</div>
          <div className="sr-kpi-value name">{isLoading ? '—' : topTeam.teamName}</div>
          <div className="sr-kpi-sub pink">{isLoading ? '' : `${topTeam.totalSubmissions} submissions`}</div>
        </div>
      </div>

      {/* ── Time Series Bar Chart ─────────── */}
      <div className="sr-chart-card">
        <div className="sr-chart-header">
          <h3>
            Submissions Over Time &mdash;&nbsp;
            <span style={{ color: '#3b5bfd' }}>{period ? `${period.charAt(0).toUpperCase() + period.slice(1)} View` : 'Daily View'}</span>
            {scope === 'team' ? ' (by Team)' : ' (by Recruiter)'}
          </h3>
        </div>
        {isLoading ? (
          <div className="sr-loading">Loading chart data…</div>
        ) : chartData.length === 0 ? (
          <div className="sr-empty">No trend data available for the selected period.</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} dy={8} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} dx={-4} />
              <Tooltip
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', fontSize: '12px' }}
                cursor={{ fill: '#f8fafc' }}
                wrapperStyle={{ zIndex: 9999 }}
              />
              <Legend iconType="circle" wrapperStyle={{ paddingTop: 16, fontSize: 12 }} />
              {chartKeys.slice(0, 10).map((key, i) => (
                <Bar key={key} dataKey={key} stackId={scope === 'team' ? 'a' : undefined} fill={COLORS[i % COLORS.length]} radius={scope === 'team' ? [0, 0, 0, 0] : [3, 3, 0, 0]} maxBarSize={40} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Split Panel ─────────────────── */}
      <div className="sr-split">

        {/* Left — Ranked Table */}
        <div className="sr-table-card">
          <h3>{scope === 'individual' ? '👤 Individual Performance' : '👥 Team Performance'}</h3>
          {isLoading ? (
            <div className="sr-loading">Loading…</div>
          ) : tableRows.length === 0 ? (
            <div className="sr-empty" style={{ height: 100 }}>No data available</div>
          ) : (
            <div className="sr-table-scroll">
              <table className="sr-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{scope === 'individual' ? 'Recruiter' : 'Team'}</th>
                    {scope === 'individual' && <th>Team</th>}
                    <th>Submissions</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, i) => (
                    <tr key={i}>
                      <td><span className={`sr-rank-badge ${rankClass(i)}`}>{i + 1}</span></td>
                      <td>
                        {scope === 'individual' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button
                              className="sr-recruiter-btn"
                              onClick={() => setSelectedRecruiter({ id: row.id, name: row.name })}
                            >
                              {row.name}
                            </button>
                            <button
                              className="sr-row-download-btn"
                              onClick={() => downloadRecruiterReport(row)}
                              title={`Download ${row.name}'s report`}
                            >
                              <FiDownload size={12} />
                            </button>
                          </div>
                        ) : (
                          <button
                            className="sr-recruiter-btn"
                            onClick={() => { setScope('individual'); setSelectedTeamFilter(row.name); }}
                            title="Click to view recruiters in this team"
                          >
                            {row.name}
                          </button>
                        )}
                      </td>
                      {scope === 'individual' && <td style={{ color: '#64748b' }}>{row.team}</td>}
                      <td><span className="sr-sub-badge">{row.submissions}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right — Pie Chart */}
        <div className="sr-pie-card">
          <h3>🥧 Team Distribution</h3>
          {isLoading ? (
            <div className="sr-loading">Loading…</div>
          ) : pieData.length === 0 ? (
            <div className="sr-empty" style={{ height: 150 }}>No data</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={95}
                    dataKey="value"
                    labelLine={false}
                    label={renderPieLabel}
                    onClick={(entry) => { setScope('individual'); setSelectedTeamFilter(entry.name); }}
                    style={{ cursor: 'pointer' }}
                  >
                    {pieData.map((_, i) => (
                      <Cell
                        key={i}
                        fill={COLORS[i % COLORS.length]}
                        opacity={selectedTeamFilter && selectedTeamFilter !== pieData[i].name ? 0.45 : 1}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', fontSize: '12px' }}
                    formatter={(val, name) => [`${val} submissions`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="sr-pie-legend">
                {pieData.map((t, i) => (
                  <div
                    key={i}
                    className="sr-legend-item"
                    onClick={() => { setScope('individual'); setSelectedTeamFilter(t.name === selectedTeamFilter ? null : t.name); }}
                    style={{ cursor: 'pointer', opacity: selectedTeamFilter && selectedTeamFilter !== t.name ? 0.45 : 1 }}
                  >
                    <span className="sr-legend-dot" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="sr-legend-label">{t.name}</span>
                    <span className="sr-legend-count">{t.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          {selectedTeamFilter && (
            <p style={{ margin: '10px 0 0', fontSize: '11px', color: '#3b5bfd', textAlign: 'center' }}>
              Filtered by: <strong>{selectedTeamFilter}</strong> — click a slice or legend to clear
            </p>
          )}
        </div>

      </div>
    </div>
  );
};

export default SubmissionReport;
